"use client";

import { z } from "zod";
import parsePhoneNumberFromString from "libphonenumber-js";
import { IZapierForm } from "@/sanity/globals/ZapierForm";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shadcn/ui/form";
import { Input } from "@/shadcn/ui/input";
import { Button } from "@/shadcn/ui/button";
import { stegaClean } from "@sanity/client/stega";
import { submitFormDataToZapier } from "@/helpers/zapier";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { cn } from "@/lib/utils";
import Loader from "@/components/global/Loader";

export default function ZapierForm({
  formData,
  theme = "default",
}: {
  formData: IZapierForm;
  theme?: "default" | "new";
}) {
  const isNewTheme = theme === "new";
  const postHog = usePostHog();
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const cleanFormData = useMemo(() => {
    return stegaClean(formData);
  }, [formData]);

  const formFields = useMemo(() => {
    return cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]:
          item.type === "text" || item.type === "textarea"
            ? item.required
              ? z.string().min(1, { message: "Erforderlich" })
              : z.string()
            : item.type === "email"
              ? item.required
                ? z
                    .string()
                    .min(1, { message: "Erforderlich" })
                    .email({ message: "Ungültige E-Mail" })
                : z.string().email({ message: "Ungültige E-Mail" }).optional()
              : item.type === "phone"
                ? z.string().transform((arg, ctx) => {
                    const phone = parsePhoneNumberFromString(arg, {
                      defaultCountry: "DE",
                      extract: false,
                    });

                    if (phone && phone.isValid()) {
                      return phone.number;
                    }

                    if (
                      (arg === "" || arg === null || arg === undefined) &&
                      !item.required
                    ) {
                      return arg;
                    }

                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: item.required
                        ? "Erforderlich"
                        : "Ungültige Telefonnummer",
                    });

                    return z.NEVER;
                  })
                : item.type === "number"
                  ? item.required
                    ? z.coerce
                        .number({
                          invalid_type_error: "Ungültige Zahl",
                        })
                        .min(0, { message: "Zahl muss positiv sein" })
                    : z.coerce
                        .number({
                          invalid_type_error: "Ungültige Zahl",
                        })
                        .min(0, { message: "Zahl muss positiv sein" })
                        .optional()
                  : item.type === "checkbox"
                    ? item.required
                      ? z.literal(true, {
                          errorMap: () => ({
                            message: "Erforderlich",
                          }),
                        })
                      : z.boolean()
                    : z.unknown(),
      };
    }, {});
  }, [cleanFormData]);

  const formSchema = useMemo(() => {
    return z.object(formFields);
  }, [formFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]: item.type === "checkbox" ? false : "",
      };
    }),
  });

  async function onSubmit(fieldData: any) {
    if (cleanFormData && fieldData) {
      setLoading(true);
      if (fieldData.email) {
        postHog.identify(fieldData.email);
      }
      postHog.capture("form_submit", {
        form: cleanFormData._id,
        formTitle: cleanFormData.name,
        fields: fieldData,
      });
      const res = await submitFormDataToZapier(fieldData, formData);
      if (res?.success === true) {
        sendGTMEvent({
          event: "zapierFormSubmit",
          "zapier-form-url": cleanFormData?.settings?.url ?? null,
          pageName: document.title,
          pagePath: window.location.href,
          "form-firstname":
            fieldData?.firstName ?? fieldData?.vorname ?? fieldData?.name,
          "form-email": fieldData?.email,
        });

        if (cleanFormData?.settings?.onSubmit === "message") {
          setSuccessMessage(
            cleanFormData?.settings?.message ?? "Form submitted successfully",
          );
          setLoading(false);
        } else if (cleanFormData?.settings?.onSubmit === "redirect") {
          const redirectUrl = resolvePageLink(
            cleanFormData?.settings?.redirect ?? null,
          );
          if (redirectUrl) {
            window.location.href = redirectUrl;
          } else {
            setSuccessMessage(
              `Form submitted successfully. Could not redirect to page ${redirectUrl}`,
            );
          }
        }
      } else {
        setLoading(false);
        setErrorMessage("Form submission failed. Try again later.");
      }
    }
  }

  return loading ? (
    <div className="flex justify-center items-center w-full h-[20rem]">
      <Loader className="text-gray-400 h-[8rem] w-[8rem]" />
    </div>
  ) : (
    <>
      {cleanFormData?.fields ? (
        <Form {...form}>
          <form
            className={cn(
              "w-full flex flex-col justify-start items-stretch relative max-w-full",
              isNewTheme ? "gap-[1rem]" : "gap-[2rem]",
            )}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {cleanFormData?.fields?.map((formField, index) => {
              return (
                <FormField
                  key={index}
                  control={form.control}
                  // @ts-ignore - this is a valid prop
                  name={formField.name}
                  disabled={loading}
                  render={({ field }) => (
                    <FormItem
                      className={
                        formField.type === "checkbox"
                          ? "flex items-start space-x-3"
                          : ""
                      }
                    >
                      {formField.type === "checkbox" ? (
                        <>
                          <FormControl>
                            <input
                              type="checkbox"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="flex-shrink-0 w-[2rem] h-[2rem] translate-y-[.125rem]"
                            />
                          </FormControl>
                          <div className="!mt-0 !ml-[1rem]">
                            <FormLabel className="font-normal text-[1.5rem]">
                              <p className="mb-0">{formField.label}</p>
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </>
                      ) : (
                        <>
                          {/* <FormLabel>{formField.label}</FormLabel> */}
                          <FormControl>
                            <Input
                              type={
                                formField.type === "number"
                                  ? "number"
                                  : formField.type
                              }
                              min={formField.type === "number" ? 0 : undefined}
                              placeholder={formField.placeholder}
                              theme={isNewTheme ? "new" : "default"}
                              {...field}
                              value={undefined}
                              className="!py-[1.5rem]"
                            />
                          </FormControl>
                          <FormMessage className="text-sm text-red-500" />
                        </>
                      )}
                    </FormItem>
                  )}
                />
              );
            })}
            <div
              className={cn(
                "relative z-10",
                isNewTheme ? "w-full" : "lg:px-[10rem] mt-6",
              )}
            >
              {isNewTheme ? (
                <>
                  <div className="relative corner-button-text">
                    <div className="transition-all duration-300 corner-button-text-initial">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                    <div className="absolute transition-all duration-300 pointer-events-none corner-button-text-hover top-[3rem]">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                  </div>

                  <div className="corner-button-divider h-[4rem]" />

                  <div className="corner-button-icon relative [&_svg]:w-full overflow-clip">
                    <div className="transition-all duration-300 corner-button-icon-initial w-[3rem] h-[3rem]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="2 8.5 20.3 23"
                      >
                        <path
                          fill="currentColor"
                          d="M2.34998 20h2.8v2.8h-2.8zM2.34998 22.8h2.8v2.8h-2.8zM2.34998 25.6h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M2.34998 28.4h2.8v2.8h-2.8zM5.15015 17.2001h2.8v2.8h-2.8zM7.95007 14.4001h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M10.75 14.4001h2.8v2.8h-2.8zM13.55 8.80005h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M13.5499 14.4001h2.8v2.8h-2.8zM13.5499 20.0001h2.8v2.8h-2.8zM16.3499 11.6h2.8v2.8h-2.8zM16.3499 17.2001h2.8v2.8h-2.8zM19.1501 14.4001h2.8v2.8h-2.8z"
                        />
                      </svg>
                    </div>
                    <div className="absolute transition-all duration-300 pointer-events-none corner-button-icon-hover top-[2.25rem] -left-[2.25rem] w-[3rem] h-[3rem]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="2 8.5 20.3 23"
                      >
                        <path
                          fill="currentColor"
                          d="M2.34998 20h2.8v2.8h-2.8zM2.34998 22.8h2.8v2.8h-2.8zM2.34998 25.6h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M2.34998 28.4h2.8v2.8h-2.8zM5.15015 17.2001h2.8v2.8h-2.8zM7.95007 14.4001h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M10.75 14.4001h2.8v2.8h-2.8zM13.55 8.80005h2.8v2.8h-2.8z"
                        />
                        <path
                          fill="currentColor"
                          d="M13.5499 14.4001h2.8v2.8h-2.8zM13.5499 20.0001h2.8v2.8h-2.8zM16.3499 11.6h2.8v2.8h-2.8zM16.3499 17.2001h2.8v2.8h-2.8zM19.1501 14.4001h2.8v2.8h-2.8z"
                        />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <Button
                  className="flex relative justify-center items-center w-full group gap-[1.25rem] overflow-clip"
                  variant="white"
                  action={"none"}
                >
                  {/*<div className="relative transition-all duration-200 button-text overflow-clip">
                    {formData?.settings?.submitText ?? "Submit"}
                  </div>*/}

                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
                  <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />

                  <div className="relative leading-none overflow-clip">
                    <div className="transition-transform duration-500 group-hover:-translate-y-full button-text">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                    <div className="absolute top-0 left-0 transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-text-hover">
                      {formData?.settings?.submitText ?? "Submit"}
                    </div>
                  </div>
                </Button>
              )}
            </div>
            {successMessage && (
              <div className="w-full font-bold text-green-900 bg-green-100 rounded-sm border border-green-500 py-[1rem] px-[2rem] drop-shadow-md">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="w-full font-bold text-red-900 bg-red-100 rounded-sm border border-red-500 py-[1rem] px-[2rem] drop-shadow-md">
                {errorMessage}
              </div>
            )}
          </form>
        </Form>
      ) : (
        <div className="p-20 font-bold text-blue-900 bg-gray-100 rounded-xl border-2 border-black">
          MISSING FORM DATA
        </div>
      )}
    </>
  );
}
